function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function normalizeProfile(payload) {
  if (payload?.profile && typeof payload.profile === 'object') {
    return payload.profile;
  }

  if (payload?.data?.profile && typeof payload.data.profile === 'object') {
    return payload.data.profile;
  }

  if (payload && typeof payload === 'object') {
    return payload;
  }

  return null;
}

function normalizeBoolean(value) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  const normalized = String(value || '').trim().toLowerCase();
  if (['true', '1', 'yes', 'on'].includes(normalized)) {
    return true;
  }

  if (['false', '0', 'no', 'off', ''].includes(normalized)) {
    return false;
  }

  return null;
}

function normalizeList(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === 'object') {
    return Object.values(value);
  }

  return [];
}

function normalizeLegalAcceptances(profile, payload) {
  const candidates = [
    profile?.legalAcceptances,
    profile?.legal_acceptances,
    payload?.legalAcceptances,
    payload?.legal_acceptances,
    payload?.data?.legalAcceptances,
    payload?.data?.legal_acceptances,
  ];

  for (const candidate of candidates) {
    const list = normalizeList(candidate).filter((item) => item && typeof item === 'object');
    if (list.length > 0) {
      return list.map((item) => ({
        type: pickFirst(item.itemType, item.item_type, item.type) || '',
        title: pickFirst(
          item.itemTitle,
          item.item_title,
          item.title,
          item.name,
          item.documentTitle,
          item.document_title,
          item.documentName,
          item.document_name,
        ) || '',
        version: pickFirst(item.itemVersion, item.item_version, item.version, item.documentVersion, item.document_version) || '',
        effectiveDate: pickFirst(
          item.itemEffectiveDate,
          item.item_effective_date,
          item.effectiveDate,
          item.effective_date,
        ) || '',
        acceptedAt: pickFirst(item.acceptedAt, item.accepted_at) || '',
        consentIp: pickFirst(item.consentIp, item.consent_ip) || '',
        status: pickFirst(item.currentStatus, item.current_status, item.status) || '',
        source: item,
      }));
    }
  }

  return [];
}

export function mapProfileResponseToDto(payload) {
  const profile = normalizeProfile(payload);
  if (!profile) {
    return null;
  }

  const firstName = pickFirst(profile.firstName, profile.first_name);
  const lastName = pickFirst(profile.lastName, profile.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return {
    id: String(pickFirst(profile.uid, profile.id) || '').trim(),
    name: fullName || pickFirst(profile.name, profile.full_name, profile.username) || '-',
    email: pickFirst(profile.emailMasked, profile.email_masked) || '-',
    emailMasked: pickFirst(profile.emailMasked, profile.email_masked) || '',
    phone: pickFirst(profile.phoneMasked, profile.phone_masked) || '-',
    phoneMasked: pickFirst(profile.phoneMasked, profile.phone_masked) || '',
    country: pickFirst(profile.country, profile.countryName, profile.country_name) || '-',
    language: pickFirst(profile.language) || '-',
    status: pickFirst(profile.status) || '-',
    invalid: normalizeBoolean(profile.invalid) === true,
    marketingConsent: normalizeBoolean(profile.marketingConsent),
    marketingConsentAt: pickFirst(profile.marketingConsentAt, profile.marketing_consent_at) || '',
    consentWithdrawnAt: pickFirst(profile.consentWithdrawnAt, profile.consent_withdrawn_at) || '',
    closureRequestedAt: pickFirst(profile.closureRequestedAt, profile.closure_requested_at) || '',
    legalHoldUntil: pickFirst(profile.legalHoldUntil, profile.legal_hold_until) || '',
    vulnerabilityFlag: normalizeBoolean(profile.vulnerabilityFlag),
    vulnerabilityNotes: pickFirst(profile.vulnerabilityNotes, profile.vulnerability_notes) || '',
    legalAcceptances: normalizeLegalAcceptances(profile, payload),
    source: profile,
  };
}
